import { AsyncPipe } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, Pipe, PipeTransform } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NzAutocompleteModule } from 'ng-zorro-antd/auto-complete';
import { NzInputModule } from 'ng-zorro-antd/input';
import { BehaviorSubject, debounceTime, Observable, of, Subject, switchMap } from 'rxjs';
import { CrmSelectOption } from 'common-module/select';
import { CrmControlValueAccessorDirective, crmControlValueAccessorFactory } from 'common-module/core';

@Pipe({
  name: 'pick',
  standalone: true
})
export class PickPipe implements PipeTransform {
  public transform(value: any[] | null, prop: string): any {
    if (!value) {
      return [];
    }
    return value.map((v) => v[prop]);
  }
}

export type AutocompleteFunction = (value: string) => Observable<CrmSelectOption[]>;

@Component({
  selector: 'app-input-group',
  template: `
    <form class="input-group">
      <nz-input-group
        class="input-tags__input-group"
        nzSearch
        nzSize="large"
      >
        <input
          type="text"
          nz-input
          [placeholder]="placeholder"
          name="tag-value"
          [nzAutocomplete]="autocomplete"
          [(ngModel)]="inputValue"
          (ngModelChange)="change()"
          (input)="onInputChange($event)"
        />
        <nz-autocomplete
          [nzDataSource]="autocompleteOptions$ | async | pick: 'label'"
          #autocomplete
        ></nz-autocomplete>
      </nz-input-group>
    </form>
  `,
  standalone: true,
  imports: [NzInputModule, NzAutocompleteModule, FormsModule, PickPipe, AsyncPipe],
  providers: [crmControlValueAccessorFactory(InputButtonGroupComponent)]
})
export class InputButtonGroupComponent extends CrmControlValueAccessorDirective implements OnInit {
  @Input() public btnLabel?: string;
  @Input() public placeholder?: string;
  @Input() public componentClass?: string;
  @Input() public autocompleteFunction?: AutocompleteFunction;
  @Output() public btnClicked = new EventEmitter<void>();

  public autocompleteOptions$ = new BehaviorSubject<CrmSelectOption[]>([]);
  public inputValue?: string;

  private inputValue$ = new Subject<string>();

  public override ngOnInit() {
    super.ngOnInit();

    this.inputValue$
      .pipe(
        debounceTime(500),
        switchMap((input: string) => (this.autocompleteFunction ? this.autocompleteFunction(input) : of([])))
      )
      .subscribe((options) => {
        this.autocompleteOptions$.next(options);
      });
  }

  public onInputChange(event: Event) {
    const input = (event.target as HTMLInputElement).value;
    this.inputValue$.next(input);
  }

  public override change() {
    const { value: options } = this.autocompleteOptions$;
    const valueOption = options.find((v) => v.label === this.inputValue);

    this.value = this.autocompleteFunction ? valueOption : this.inputValue;
    super.change(this.value);
    if (this.value) {
      this.inputValue = undefined;
    }
  }

  public override writeValue(obj: any) {
    super.writeValue(obj);
    this.inputValue = obj?.label;
  }

  public handleButtonClick() {
    this.btnClicked.emit();
  }
}
